import {CommandItem} from '@/base/ui/command'
import {WuCheckbox} from '@/components/checkbox'
import {cn} from '@/lib/utils'
import React, {useEffect, useState} from 'react'
import {IWuComboboxItem} from '../types/IWuComboboxOptions'

export interface IWuComboboxItemProps<T> {
  option: IWuComboboxItem<T>
  accessorKey: {value: string; label: string}
  value?: IWuComboboxItem<T> | IWuComboboxItem<T>[]
  multiple?: boolean
  onSelect: (value: IWuComboboxItem<T>) => void
  dir?: 'ltr' | 'rtl'
}

export const WuComboboxItem = <T,>({
  option,
  value,
  multiple = false,
  onSelect,
  accessorKey,
  dir,
}: IWuComboboxItemProps<T>): React.JSX.Element => {
  const [selected, setSelected] = useState<string | string[]>(
    multiple ? [] : '',
  )

  useEffect(() => {
    if (value) {
      if (multiple) {
        const temp = value as IWuComboboxItem<T>[]
        setSelected(
          temp.map(item => JSON.stringify(item[accessorKey.value as keyof T])),
        )
      } else {
        setSelected(
          JSON.stringify(
            (value as IWuComboboxItem<T>)[accessorKey.value as keyof T],
          ),
        )
      }
    }
  }, [value, accessorKey.value, multiple])

  const isSelected = multiple
    ? (selected as string[]).includes(
        JSON.stringify(option[accessorKey.value as keyof T]),
      )
    : selected === JSON.stringify(option[accessorKey.value as keyof T])

  const handleClick = (): void => {
    if (!option.disabled) {
      onSelect(option)
    }
  }

  return (
    <CommandItem
      onSelect={handleClick}
      disabled={option.disabled}
      className={cn(
        'cursor-pointer text-xs h-8 py-0 wu-select-item hover:bg-gray-100',
        isSelected && 'bg-gray-10',
      )}
      dir={dir}
    >
      {multiple && <WuCheckbox checked={isSelected} />}
      <ItemChildren option={option} accessorKey={accessorKey} />
    </CommandItem>
  )
}

const ItemChildren = <T,>({
  option,
  accessorKey,
}: Pick<
  IWuComboboxItemProps<T>,
  'option' | 'accessorKey'
>): React.JSX.Element => {
  const label = option[accessorKey.label as keyof T] as string

  return (
    <div className="flex items-center w-full gap-1">
      {option.icon && option.icon}
      {label}
      {option.isAdmin && (
        <span className="ml-auto text-sm wm-lock text-orange"></span>
      )}
    </div>
  )
}
